import { useState } from 'react'
import { useCartContext } from 'src/lib/contexts/CartContext'
import { RupeeIcon, MilestoneCategory, Milestone } from '@shopflo/ui'
import { X } from 'react-feather'
import ConfirmationDialog from 'src/components/common/feedback-and-dialog/ConfirmationDialog'
import { useLocale } from 'src/lib/hooks/useLocal'
import { useCartUIPermissions } from 'src/lib/access-permission-hooks/useCartUIPermissions'
import { errorToast } from 'src/lib/utils/toasters'

interface MilestoneItemProps {
  milestone: Milestone
  onDelete: (milestone: Milestone) => void
}

const MilestoneItem: React.FC<MilestoneItemProps> = ({ milestone, onDelete }) => {
  const {
    getters: { customizations, isAmountBased, isCountBased },
    actions: { setDraftMilestone, setIsEditingMilestone }
  } = useCartContext()

  const { canEditCart } = useCartUIPermissions()

  return (
    <li className='border-2 border-gray-light rounded-md p-3 space-y-2'>
      <div className='flex flex-row space-x-2 items-center text-coal-dark font-medium'>
        {/* {renderMileStoneIcon} */}
        <span className=''>{milestone.milestone_text}</span>
        <div className='border-1 border-l-gray h-4'></div>
        <div className='flex flex-row items-center'>
          {isAmountBased && <RupeeIcon />}
          <span className=''>{milestone.milestone_amount}</span>
        </div>
        <button
          className={`!ml-auto cursor-${canEditCart ? 'pointer' : 'not-allowed'}`}
          disabled={!canEditCart}
          onClick={() => onDelete(milestone)}
        >
          <X className='w-4 h-4 text-coal-dark' />
        </button>
      </div>
      <div className='flex flex-row items-center text-coal text-[#858585]'>
        <span className='text-sm font-medium truncate'>
          {milestone.milestone_header.replace(/{{price}}|{{value}}/g, '<value>')}
        </span>
      </div>
      <button
        className={`text-sm font-semibold text-coal-dark underline cursor-${canEditCart ? 'pointer' : 'not-allowed'}`}
        disabled={!canEditCart}
        onClick={() => {
          setDraftMilestone(milestone)
          setIsEditingMilestone(true)
        }}
      >
        Edit
      </button>
    </li>
  )
}

const Milestones = () => {
  const { t } = useLocale()
  const {
    getters: { milestones },
    actions: { setMilestones }
  } = useCartContext()

  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState(false)
  const [milestoneToDelete, setMilestoneToDelete] = useState<Milestone | null>(null)

  const handleDelete = (milestone: Milestone) => {
    if (milestones.length <= 1) {
      errorToast('At least one milestone is required')
      return
    }

    setMilestoneToDelete(milestone)
    setConfirmDeleteDialog(true)
  }

  const deleteMilestone = async () => {
    if (milestoneToDelete) {
      const filteredMilestones = milestones.filter((m: Milestone) => m.milestone !== milestoneToDelete.milestone)
      setMilestones(filteredMilestones)
      setMilestoneToDelete(null)
      setConfirmDeleteDialog(false)
    }
  }

  return (
    <>
      <ul className='list-none w-96 space-y-4'>
        {milestones.map((milestone: Milestone) => (
          <MilestoneItem key={milestone.milestone} milestone={milestone} onDelete={handleDelete} />
        ))}
      </ul>
      <ConfirmationDialog
        isOpen={confirmDeleteDialog}
        setIsConfirmationOpen={setConfirmDeleteDialog}
        title={t('delete_milestone_confirmation_text', { milestone_amount: milestoneToDelete?.milestone_amount })}
        confirmationText={
          'Deleting this milestone will remove it from the progress bar. Are you sure you want to delete?'
        }
        jobButton='Delete'
        cancelButton='Cancel'
        jobButtonColor='error'
        cancelButtonColor='primary'
        handleJob={deleteMilestone}
      />
    </>
  )
}

export default Milestones
